import { BaseService } from '@/core/base/base.service';
import { AppConstants } from '@/core/utils/app.const';
import { CollaboratorFilter } from '@/models/filter/collaborator-filter';
import { inject, Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { finalize, Observable, tap } from 'rxjs';
import { P360MyFormModel } from './components/dynamic-form/model/dynamic-my-forms';
import { AppStorage } from './core/utils/app.storage';
import { ResponseDto } from './models/app.response-dto';
import { CampaignResource } from './models/by-name/campaign-resource';
import { P360DropdownItem } from './models/common';
import { Dispatcher } from './models/dispatcher';
import { Gen0000 } from './models/general-information/gen-0000.global-catalog';
import { Gen0001 } from './models/general-information/gen-0001.customer';
import { Gen0022 } from './models/general-information/gen-0022.segment';
import { Gen0039 } from './models/general-information/gen-0039.specialty';
import { Gen0040 } from './models/general-information/gen-0040.active-campaign';
import { Gen0041 } from './models/general-information/gen-0041.social-media';
import { Line } from './models/line';
import { ProductItem } from './models/product-item';
import { CampaignModel } from './pages/admin/campaign/model/admin.campaign';
import { appStoreFiltersActions } from './store/filters/store.filters.action';
import { appStoreGlobalActions } from './store/global/store.global.action';

@Injectable({
  providedIn: 'root',
})
export class AppService extends BaseService {
  private _loadInfoCounter = 0;

  private _campaignsLoadedTotal = 0;
  private _campaignsLoadedCountrer = 0;

  private readonly ngZone = inject(NgZone);

  handlePositionPermission = () => {
    this.handleCheckPositionPermission().then();

    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        this.ngZone.run(() => {
          this.handleCheckPositionPermission().then();
        });
      }
    });
  };

  handleRefreshCampaigns = (agentCode: string, isRefresh = false) => {
    this.store.dispatch(appStoreGlobalActions.setCampaignsLoadingAction({ value: true }));
    this.loadActiveCampaigns(agentCode, isRefresh);
  };

  handleRefreshDynamicForms = (isRefresh = false) => {
    this.store.dispatch(
      appStoreGlobalActions.setDynamicFormsAction({
        myForms: [],
        isRefresh,
      }),
    );
    this.loadMyForms(isRefresh);
  };

  handleRefreshInformation = (agentCode: string, isRefresh = false) => {
    this._loadInfoCounter = 1;
    this.whoAmI().subscribe(() => {
      this.messageService.addSuccess('MESSAGE.200_PREFIX#MESSAGE.200_SYNC_WHO_AM_I', {}, true);
    });
    this.loadGen0000(agentCode, isRefresh);
    this.loadItems(agentCode, isRefresh);
    this.loadDispatchers(agentCode, isRefresh);
    this.loadCollaborators(agentCode, isRefresh);
    this.loadClients(agentCode, isRefresh);
    this.loadClientsForFilters(agentCode, isRefresh);
    this.loadLines(agentCode, isRefresh);
    this.loadSegment(agentCode, isRefresh);
    this.loadSpecialties(agentCode, isRefresh);
    this.loadSocialMedias(agentCode, isRefresh);
  };

  loadGen0000 = (document: string, isRefresh = false) => {
    this.storage.get(AppStorage.GLOBAL_CATALOGS).then((globalCatalogs) => {
      if (isRefresh || globalCatalogs == null) {
        this.getGen0000({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: ({ results }) => {
              this.storage.remove(AppStorage.GLOBAL_CATALOGS);
              if (results && results.length > 0) {
                this.storage.set(AppStorage.GLOBAL_CATALOGS, results);
                this.store.dispatch(
                  appStoreFiltersActions.setGlobalCatalogListAction({
                    globalCatalogList: results,
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setGlobalCatalogListAction({
            globalCatalogList: globalCatalogs,
          }),
        );
      }
    });
  };

  loadItems = (document: string, isRefresh = false) => {
    this.storage.get<ProductItem[]>(AppStorage.PRODUCT_ITEMS).then((productItems) => {
      if (isRefresh || productItems == null) {
        this.getGen0002({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: ({ results }) => {
              this.storage.remove(AppStorage.PRODUCT_ITEMS);
              if (results && results.length > 0) {
                this.storage.set(AppStorage.PRODUCT_ITEMS, results);
                this.store.dispatch(
                  appStoreFiltersActions.setGlobalProductItemListAction({
                    productItemList: results,
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setGlobalProductItemListAction({
            productItemList: productItems,
          }),
        );
      }
    });
  };

  loadDispatchers = (document: string, isRefresh = false) => {
    this.storage.get<Dispatcher[]>(AppStorage.DISPATCHERS).then((dispatchers) => {
      if (isRefresh || dispatchers == null) {
        this.getGen0030({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: ({ results }) => {
              this.storage.remove(AppStorage.DISPATCHERS);
              if (results && results.length > 0) {
                this.storage.set(AppStorage.DISPATCHERS, results);
                this.store.dispatch(
                  appStoreFiltersActions.setGlobalDispatcherListAction({
                    dispatcherList: results,
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setGlobalDispatcherListAction({
            dispatcherList: dispatchers,
          }),
        );
      }
    });
  };

  loadCollaborators = (document: string, isRefresh = false) => {
    this.storage.get<P360DropdownItem<CollaboratorFilter>[]>(AppStorage.COLLABORATORS).then((collaborators) => {
      if (isRefresh || collaborators == null) {
        this.getCollaborators({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: (data) => {
              const collaborators =
                data.results?.map((item: any) => ({
                  ...item,
                  isNew: false,
                })) ?? [];

              const firstItem = {
                id: 0,
                label: 'ALL',
                value: {
                  id: 0,
                  name: 'ALL',
                  code: 'TODOS',
                },
              };
              const collaboratorFilterList: P360DropdownItem<CollaboratorFilter>[] = [
                firstItem,
                ...collaborators.map((item, idx) => ({
                  id: idx + 1,
                  label: item.name,
                  value: { id: idx + 1, ...item },
                })),
              ];

              this.storage.remove(AppStorage.COLLABORATORS);
              if (collaborators.length > 0) {
                this.storage.set(AppStorage.COLLABORATORS, collaboratorFilterList);
                this.store.dispatch(
                  appStoreFiltersActions.setCollaboratorListAction({
                    collaboratorFilterList,
                  }),
                );
              } else {
                this.store.dispatch(
                  appStoreFiltersActions.setCollaboratorListAction({
                    collaboratorFilterList: [firstItem],
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setCollaboratorListAction({
            collaboratorFilterList: collaborators,
          }),
        );
      }
    });
  };

  loadClientsForFilters = (document: string, isRefresh = false, showToast = true) => {
    this.storage.get<P360DropdownItem<Gen0001>[]>(AppStorage.CLIENTS_FOR_FILTERS).then((clients) => {
      if (isRefresh || clients == null) {
        this.getGen0019({ document }, showToast)
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: (data) => {
              const clientForFiltersFilterList =
                data.results?.map((item, idx) => ({
                  id: idx + 1,
                  label: item.name,
                  value: { id: idx + 1, ...item },
                })) ?? [];

              this.storage.remove(AppStorage.CLIENTS_FOR_FILTERS);
              if (clientForFiltersFilterList.length > 0) {
                this.storage.set(AppStorage.CLIENTS_FOR_FILTERS, clientForFiltersFilterList);

                this.store.dispatch(
                  appStoreFiltersActions.setClientForFiltersListAction({
                    clientForFiltersFilterList,
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setClientForFiltersListAction({
            clientForFiltersFilterList: clients,
          }),
        );
      }
    });
  };

  loadLines = (document: string, isRefresh = false) => {
    this.storage.get<P360DropdownItem<Line>[]>(AppStorage.LINES).then((lines) => {
      if (isRefresh || lines == null) {
        this.getGen0021({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: (data) => {
              const lineFilterList: P360DropdownItem<any>[] =
                data.results?.map((item: any, idx: number) => ({
                  id: idx + 1,
                  label: item.name,
                  value: { id: idx + 1, ...item },
                })) ?? [];

              this.storage.remove(AppStorage.LINES);
              if (lineFilterList.length > 0) {
                this.storage.set(AppStorage.LINES, lineFilterList);

                this.store.dispatch(
                  appStoreFiltersActions.setLineListAction({
                    lineFilterList,
                  }),
                );
              } else {
                this.store.dispatch(
                  appStoreFiltersActions.setLineListAction({
                    lineFilterList: [
                      {
                        id: -1,
                        label: 'ALL',
                        value: {
                          id: -1,
                          name: 'ALL',
                          code: 'TODOS',
                        },
                      },
                    ],
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setLineListAction({
            lineFilterList: lines,
          }),
        );
      }
    });
  };

  loadSegment = (document: string, isRefresh = false) => {
    this.storage.get<P360DropdownItem<Gen0022>[]>(AppStorage.SEGMENTS).then((segments) => {
      if (isRefresh || segments == null) {
        this.getGen0022({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: (data) => {
              const segmentFilterList: P360DropdownItem<Gen0022>[] =
                data.results?.map((item: Gen0022, idx: number) => ({
                  id: idx + 1,
                  label: item.name,
                  value: { id: idx + 1, ...item },
                })) ?? [];
              this.storage.remove(AppStorage.SEGMENTS);
              if (segmentFilterList.length > 0) {
                this.storage.set(AppStorage.SEGMENTS, segmentFilterList);
                this.store.dispatch(
                  appStoreFiltersActions.setSegmentListAction({
                    segmentFilterList,
                  }),
                );
              } else {
                this.store.dispatch(
                  appStoreFiltersActions.setSegmentListAction({
                    segmentFilterList: [
                      {
                        id: -1,
                        label: 'ALL',
                        value: {
                          id: -1,
                          name: 'ALL',
                          code: 'TODOS',
                        },
                      },
                    ],
                  }),
                );
              }
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setSegmentListAction({
            segmentFilterList: segments,
          }),
        );
      }
    });
  };

  loadActiveCampaigns = (document: string, isRefresh = false) => {
    this.storage.get<Gen0040[]>(AppStorage.ACTIVE_CAMPAIGNS).then((activeCampaigns) => {
      if (isRefresh || activeCampaigns == null) {
        this.getGen0040({ document }).subscribe({
          next: (data) => {
            const activeCampaigns = data.results;
            this.storage.remove(AppStorage.ACTIVE_CAMPAIGNS);
            if (activeCampaigns && activeCampaigns?.length > 0) {
              this.storage.set(AppStorage.ACTIVE_CAMPAIGNS, activeCampaigns);
              this.loadAllCampaignResources(activeCampaigns, isRefresh);
            }
          },
          error: (error) => {
            if (error.status === 204) {
              this.storage.remove(AppStorage.ACTIVE_CAMPAIGNS);
              this._campaignsLoadedTotal = 0;
              this._campaignsLoadedCountrer = 0;
              this.store.dispatch(appStoreGlobalActions.setCampaignsLoadingAction({ value: false }));
            }
          },
        });
      } else {
        this.loadAllCampaignResources(activeCampaigns, isRefresh);
      }
    });
  };

  loadSpecialties = (document: string, isRefresh = false) => {
    this.storage.get<Gen0039[]>(AppStorage.SPECIALTIES).then((specialties) => {
      if (isRefresh || specialties == null) {
        this.getGen0039({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: ({ results: specialties }) => {
              this.storage.remove(AppStorage.SPECIALTIES);
              this.storage.set(AppStorage.SPECIALTIES, specialties);
              this.store.dispatch(
                appStoreFiltersActions.setSpecialtiesAction({
                  specialties: specialties ?? [],
                }),
              );
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setSpecialtiesAction({
            specialties,
          }),
        );
      }
    });
  };

  loadAllCampaignResources = (activeCampaigns: Gen0040[], isRefresh = false) => {
    const resourceIds = [...new Set(activeCampaigns.map((item) => item.resource))];

    const campaignIds = [...new Set(activeCampaigns.map((item) => item.campaign))];

    this.store.dispatch(
      appStoreGlobalActions.setActiveCampaignsAction({
        activeCampaigns,
        campaignIds,
        resourceIds,
      }),
    );

    this._campaignsLoadedTotal = campaignIds.length;
    this._campaignsLoadedCountrer = 0;
    this.store.dispatch(appStoreGlobalActions.setCampaignsLoadingAction({ value: true }));

    campaignIds.forEach((item) => {
      this.loadCampaignResource(item, isRefresh);
    });
  };

  loadCampaignResource = (campaign: number, isRefresh = false) => {
    const storageCampaignName = AppStorage.CAMPAIGNS.replace('{ID}', campaign.toString());

    this.storage.get<CampaignModel>(storageCampaignName).then((campaignView) => {
      if (isRefresh || campaignView == null) {
        this.loadCampaignResourceApi(campaign);
      } else if (campaignView) {
        this.store.dispatch(
          appStoreGlobalActions.setCampaignAction({
            id: campaignView.id!,
            campaign: campaignView,
          }),
        );

        const storageCampaignResourcesName = AppStorage.CAMPAIGN_RESOURCES.replace(
          '{ID}',
          campaignView.resourceId?.toString() ?? '',
        );
        this.storage.get<CampaignResource[]>(storageCampaignResourcesName).then((resources) => {
          if (resources == null) {
            this.loadCampaignResourceApi(campaign);
          } else {
            this.store.dispatch(
              appStoreGlobalActions.setCampaignResourcesAction({
                id: campaignView.resourceId!,
                resources,
              }),
            );
            this.increaseCampaignsLoadedCounter(isRefresh);
          }
        });
      }
    });
  };

  loadSocialMedias = (document: string, isRefresh = false) => {
    this.storage.get<Gen0041[]>(AppStorage.SOCIAL_MEDIAS).then((socialMediaList) => {
      if (isRefresh || socialMediaList == null) {
        this.getGen0041({ document })
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: ({ results: socialMediaList }) => {
              this.storage.remove(AppStorage.SOCIAL_MEDIAS);
              this.storage.set(AppStorage.SOCIAL_MEDIAS, socialMediaList);
              this.store.dispatch(
                appStoreFiltersActions.setSocialMediaListAction({
                  socialMediaList: socialMediaList ?? [],
                }),
              );
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setSocialMediaListAction({
            socialMediaList,
          }),
        );
      }
    });
  };

  loadOfflineData = (document: string) => {
    this._loadInfoCounter = 1;
    this.loadGen0000(document);
    this.loadItems(document);
    this.loadDispatchers(document);
    this.loadCollaborators(document);
    this.loadClients(document);
    this.loadClientsForFilters(document);
    this.loadLines(document);
    this.loadSegment(document);
    this.loadSpecialties(document);
    this.loadSocialMedias(document);

    this.loadActiveCampaigns(document);

    this.loadMyForms();
  };

  changePassword = (username: string, password: string) => {
    const authorization = btoa(`${password}`);
    return this.rest
      .patch({
        url: `${AppConstants.SECURITY_HOST}/security/user/pass/{username}`,
        body: authorization,
        urlParams: { username },
      })
      .pipe(
        tap({
          next: (res) => {
            this.whoAmI().subscribe();
          },
        }),
      );
  };

  protected override handleLoadInfoCounter = () => {
    this._loadInfoCounter++;
    if (this._loadInfoCounter > 10) {
      this._loadInfoCounter = 0;
    }
  };

  private readonly loadCampaignResourceApi = (campaign: number) => {
    const storageCampaignName = AppStorage.CAMPAIGNS.replace('{ID}', campaign.toString());
    this.getCampaignResources({ campaign })
      .pipe(finalize(this.increaseCampaignsLoadedCounter))
      .subscribe({
        next: (data) => {
          const resources = data.results;
          const campaign = data.extraResult;

          this.storage.remove(storageCampaignName);
          if (campaign) {
            this.storage.set(storageCampaignName, campaign);
            this.store.dispatch(
              appStoreGlobalActions.setCampaignAction({
                id: campaign.id!,
                campaign,
              }),
            );
          }

          const storageCampaignResourcesName = AppStorage.CAMPAIGN_RESOURCES.replace(
            '{ID}',
            campaign?.resourceId?.toString() ?? '',
          );
          this.storage.remove(storageCampaignResourcesName);
          if (resources && resources.length > 0) {
            this.storage.set(storageCampaignResourcesName, resources);
            this.store.dispatch(
              appStoreGlobalActions.setCampaignResourcesAction({
                id: campaign?.resourceId ?? -1,
                resources,
              }),
            );
          }
        },
      });
  };

  get loadInfoCounter() {
    return this._loadInfoCounter !== 0;
  }

  private readonly getGen0000 = (params: any): Observable<{ results?: Gen0000[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0000, params);

  private readonly getGen0002 = (params: any): Observable<{ results?: ProductItem[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0002, params).pipe(
      tap({
        next: () => {
          this.messageService.addSuccess('MESSAGE.200_PREFIX#MESSAGE.200_SYNC_GEN_0002', {}, true);
        },
      }),
    );

  private readonly getGen0019 = (params: any, showToast = true): Observable<{ results?: Gen0001[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0019, params).pipe(
      tap({
        next: () => {
          if (showToast) {
            this.messageService.addSuccess('MESSAGE.200_PREFIX#MESSAGE.200_SYNC_GEN_0019', {}, true);
          }
        },
      }),
    );

  private readonly getGen0021 = (params: any): Observable<{ results?: Line[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0021, params);

  private readonly getGen0022 = (params: any): Observable<{ results?: Gen0022[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0022, params);

  private readonly getGen0030 = (params: any): Observable<{ results?: Dispatcher[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0030, params);

  private readonly getGen0039 = (params: any): Observable<ResponseDto<Gen0039>> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0039, params);

  private readonly getGen0040 = (params: any): Observable<{ results?: Gen0040[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0040, params);

  private readonly getGen0041 = (params: any): Observable<{ results?: Gen0041[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0041, params)
      .pipe
      // TODO: Fully remove this message...
      // tap({
      //   error: () => {
      //     this.messageService.addWarning('MESSAGE.204_PREFIX#MESSAGE.204_GEN_0041', {}, true);
      //   },
      // }),
      ();

  private readonly getCollaborators = (params: any): Observable<{ results?: CollaboratorFilter[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.SALESFORCE_TREE, params);

  private readonly getCampaignResources = (
    params: any,
  ): Observable<{
    code?: string;
    results?: CampaignResource[];
    extraResult?: CampaignModel;
  }> => this.restGet(AppConstants.REST_ENDPOINTS.CAMPAIGN_RESOURCE, params, null, undefined, -1);

  private readonly increaseCampaignsLoadedCounter = (isRefresh: boolean = true) => {
    this._campaignsLoadedCountrer++;
    const value = this._campaignsLoadedCountrer !== this._campaignsLoadedTotal;
    if (isRefresh && !value) {
      this.messageService.addSuccess('MESSAGE.200_PREFIX#MESSAGE.200_SYNC_GEN_0040', {}, true);
    }
    this.store.dispatch(appStoreGlobalActions.setCampaignsLoadingAction({ value }));
  };

  private readonly loadMyForms = (isRefresh = false) => {
    this.storage.get<P360MyFormModel>(AppStorage.MY_FORMS).then((myForms) => {
      if (isRefresh || myForms == null) {
        this.getMyForms().subscribe({
          next: (data) => {
            const myForms = data.result;
            this.storage.remove(AppStorage.MY_FORMS);
            if (myForms) {
              this.storage.set(AppStorage.MY_FORMS, myForms);
              this.loadMyFormsToStore(myForms);
            }
          },
        });
      } else {
        this.loadMyFormsToStore(myForms);
      }
    });
  };

  private readonly getMyForms = (): Observable<{ result?: P360MyFormModel }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.DYNAMIC_FORM, null);

  private readonly loadMyFormsToStore = (myForms: P360MyFormModel) => {
    this.store.dispatch(
      appStoreGlobalActions.setDynamicFormsAction({
        myForms: myForms.forms ?? [],
        isRefresh: false,
      }),
    );
    this.store.dispatch(
      appStoreGlobalActions.setDynamicFormsAssignedAction({
        myFormsAssigned: myForms.assignedTo ?? [],
      }),
    );
  };
}
